import React from 'react';
import { Link } from 'react-router';
import { GRIEVANCE_STATUSES, GrievanceItem } from '../../services/types/Grievance';
import { Cell } from '../../shared/UIKITComponents/Table';
import { customDateStr } from '../../shared/misc/utils';
import ImpactBadge from '../Operations/ImpactBadge';
import UIKitPerformanceChip from '../../shared/UIKITComponents/Chips/PerfonmanceChip';
import { UIKitRiskVariant } from '../../shared/UIKITComponents/Chips/RiskChip';
import RiskChipWrapper from '../../shared/UIKITComponents/Chips/RiskChipWrapper';
import { Button } from '../../shared/components';
import RiskBadge from '../Operations/RiskBadge';
import Pending from '../Operations/Pending';

const NA = 'N/A';

const ensureString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

export const formatYesNo = (value: boolean | null | undefined): string => {
  if (value === true) {
    return 'Yes';
  }
  if (value === false) {
    return 'No';
  }
  return ''; // O 'N/A' si prefieres
};

const isApplicableValue = (isApplicable: boolean | null | undefined, value: string | null) => {
  if (isApplicable === true) {
    return value;
  }
  if (isApplicable === false) {
    return NA;
  }
  return '';
};

const getCells = (
  showRationale: (rationaleType: string, rationale: string) => void,
  t: (key: string) => string
) => {
  const cells: Cell<GrievanceItem>[] = [
    // Show Columns.
    {
      name: 'number',
      value: (x) => x.number,
      render: (x, o) => <Link to={`/grievance-protocol/details/${o.number}`}>{x}</Link>,
      column: 'Grievance Number',
      css: 'S1',
    },
    {
      name: 'claimantName',
      value: (x) => x.claimantName,
      column: 'claimantName',
    },
    { name: 'grievanceGender', value: (x) => x.grievanceGender, column: 'Gender' },
    {
      name: 'countryClaimantOfResidence',
      value: (x) => x.countryClaimantOfResidence,
      column: 'countryOfResidence',
    },
    { name: 'emailClaimant', value: (x) => x.emailClaimant, column: 'Email' },
    { name: 'phoneClaimant', value: (x) => x.phoneClaimant, column: 'Mobile Phone Number' },
    {
      name: 'grievanceDescription',
      value: (x) => x.grievanceDescription,
      column: 'grievanceDescription',
    },
    {
      name: 'isApplicable',
      value: (x) => {
        if (x.isApplicable == null) {
          return '';
        }
        return x.isApplicable ? 'Yes' : 'No';
      },
      render: (x) => <span>{x}</span>,
    },
    {
      name: 'grievanceStatus',
      value: (x) =>
        x.grievanceStatus === GRIEVANCE_STATUSES.NOT_APPLICABLE
          ? t('grievanceStagesGrid.NotApplicable')
          : t(`grievanceStagesGrid.${ensureString(x.grievanceStatus)}`),
      column: 'grievanceStatus',
    },
    {
      name: 'currentPhaseName',
      value: (x) =>
        x.currentPhaseName ? t(`grievanceStagesGrid.${ensureString(x.currentPhaseName)}`) : NA,
      column: 'grievancePhase',
    },
    {
      name: 'registrationDate',
      value: (x) => customDateStr(x.registrationDate),
      column: 'Registration Date',
    },
    {
      name: 'grievanceTimeElapsed',
      value: (x) => ensureString(x.grievanceTimeElapsed),
      column: 'grievanceTimeElapsed',
      sortDisabled: true,
    },

    {
      name: 'programOrPrjNumber',
      value: (x) => ensureString(x.programOrPrjNumber),
      column: 'Program or Project Number',
    },
    {
      name: 'programOrPrjCountry',
      value: (x) => ensureString(x.programOrPrjCountry),
      column: 'Program or Project Country',
    },
    {
      name: 'programOrPrjName',
      value: (x) => ensureString(x.programOrPrjName),
      column: 'Program or Project Name',
      width: 350,
    },
    {
      name: 'operationCountry',
      value: (x) => ensureString(x.operationCountry),
      column: 'Operation Country',
    },
    {
      name: 'mainTopicName',
      width: 300,
      value: (x) => ensureString(x.mainTopicName),
    },
    {
      name: 'subTopicName',
      width: 200,
      value: (x) => ensureString(x.subTopicName),
    },
    {
      name: 'secondarySubTopicName',
      width: 200,
      value: (x) => ensureString(x.secondarySubTopicName),
    },
    {
      name: 'trackType',
      value: (x) => ensureString(x.trackType),
    },
    {
      name: 'prioritization',
      value: (x) => ensureString(x.prioritization).replace(/^\d/, ''),
    },
    {
      name: 'isConfidential',
      value: (x) => (x.isConfidential ? 'Yes' : 'No'),
      column: 'confidentiality',
    },
    {
      name: 'originPrimary',
      value: (x) => ensureString(x.originPrimary),
    },
    {
      name: 'originSecondary',
      value: (x) => ensureString(x.originSecondary),
    },
    {
      name: 'channelPrimary',
      value: (x) => ensureString(x.channelPrimary),
    },
    {
      name: 'channelSecondary',
      value: (x) => ensureString(x.channelSecondary),
    },
    {
      name: 'isPGM',
      value: (x) => formatYesNo(x.isPGM),
      render: (x) => <span>{x}</span>,
    },
    {
      name: 'noPGMReason',
      value: (x) => (x.noPGMReason ? x.noPGMReason : ''),
    },
    {
      name: 'isInminentRisk',
      value: (x) => formatYesNo(x.isInminentRisk),
      render: (x) => <span>{x}</span>,
    },
    {
      name: 'imminentRiskTypes',
      value: (x) => ensureString(x.imminentRiskTypes),
      width: 300,
      sortDisabled: true,
    },
    {
      name: 'imminentRiskTypeOther',
      value: (x) => ensureString(x.imminentRiskTypeOther),
    },
    {
      name: 'isConfidentialityChange',
      value: (x) => (x.isConfidentialityChange ? 'Yes' : 'No'),
    },
    {
      name: 'confidentialityChangeDate',
      value: (x) => customDateStr(x.confidentialityChangeDate),
    },
    {
      name: 'confidentialityChangeReason',
      value: (x) => ensureString(x.confidentialityChangeReason),
    },
    {
      name: 'isGroupClaim',
      value: (x) => (x.isGroupClaim ? 'Yes' : 'No'),
      render: (x) => <span>{x}</span>,
    },
    {
      name: 'groupClaimNumber',
      value: (x) => ensureString(x.groupClaimNumber),
    },
    {
      name: 'groupDialogueProcess',
      value: (x) => (x.groupDialogueProcess ? 'Yes' : 'No'),
      render: (x) => <span>{x}</span>,
    },
    {
      name: 'relateds',
      column: 'relatedGrievanceNumber',
      value: (x) => (x.relateds ? x.relateds.map((item) => item.number).join(', ') : NA),
      sortDisabled: true,
    },
    {
      name: 'isReopenedCase',
      value: (x) => (x.isReopenedCase ? 'Yes' : 'No'),
      render: (x) => <span>{x}</span>,
    },
    {
      name: 'previousGrievanceNumber',
      value: (x) => ensureString(x.previousGrievanceNumber),
    },
    {
      name: 'operationNumber',
      value: (x) => x.operationNumber || '',
      render: (x, o) =>
        x !== NA ? (
          <Link to={`/operations/${o.operationNumber}`} target="_blank">
            {x}
          </Link>
        ) : (
          <span>{x}</span>
        ),
      column: 'Operation Number',
    },
    {
      name: 'operationName',
      value: (x) => x.operationName,
      column: 'operationName',
    },
    {
      name: 'operationSector',
      value: (x) => x.operationSector,
      column: 'sectorSubsector',
    },
    { name: 'region', value: (x) => x.region, column: 'regionCountry' },
    {
      name: 'operationContractor',
      value: (x) => ensureString(x.operationContractor),
    },
    {
      name: 'executingAgency',
      value: (x) => ensureString(x.executingAgency),
    },
    {
      name: 'operationTeamLeader',
      value: (x) => x.operationTeamLeader,
      column: 'teamLeader',
    },
    {
      name: 'operationPrimaryMember',
      value: (x) => x.operationPrimaryMember,
      column: 'primaryMember',
    },
    {
      name: 'operationMembers',
      value: (x) => x.operationMembers,
      column: 'esgMembers',
    },
    {
      name: 'operationEsic',
      value: (x) => (x.operationEsic || '').replace('-', ''),
      column: 'esic',
      render: (x) => <ImpactBadge text={x} />,
    },
    {
      name: 'operationDccrc',
      value: (x) => x.operationDccrc,
      column: 'dccrc',
      render: (x) => <RiskBadge text={x} />,
    },
    {
      name: 'operationEsrr',
      value: (x) => x.operationEsrr,
      column: 'esrr',
      render: (x) => <RiskChipWrapper value={x as UIKitRiskVariant} />,
    },
    {
      name: 'operationSpr',
      value: (x) => (x.operationSpr ? x.operationSpr.substring(1) : null),
      column: 'spr',
      render: (x) => <UIKitPerformanceChip variant={x} />,
    },
    {
      name: 'operationSprDate',
      value: (x) => customDateStr(x.operationSprDate),
      column: 'sprDate',
    },
    {
      name: 'operationSprRationale',
      column: 'sprRationale',
      value: (x) => x.operationSprRationale,
      sortDisabled: true,
      render: (v, obj) => (
        <Button
          htmlId={`spr_button_${obj.id}`}
          className="btn-tertiary no-padding"
          icon="clipboard-list"
          text=""
          onClick={() => showRationale('operationSprRationale', v)}
          title={t('rationaleDetails')}
        />
      ),
    },
    {
      name: 'previousSPR',
      value: (x) => (x.previousSPR ? x.previousSPR.substring(1) : null),
      column: 'prevSpr',
      render: (x) => <UIKitPerformanceChip variant={x} />,
    },
    {
      name: 'previousSPRDate',
      value: (x) => customDateStr(x.previousSPRDate),
      column: 'prevSprDate',
      dependsOn: 'previousSPR',
    },
    {
      name: 'previousSPRRationale',
      value: (x) => x.previousSPRRationale,
      column: 'prevSprRationale',
      dependsOn: 'previousSPR',
      render: (v, obj) => (
        <Button
          htmlId={`spr_button_${obj.id}`}
          className="btn-tertiary no-padding"
          icon="clipboard-list"
          text=""
          onClick={() => showRationale('previousSPRRationale', v)}
          title={t('rationaleDetails')}
        />
      ),
    },
    {
      name: 'espsPoliciesWithRequirements',
      value: (x) => x.espsPoliciesWithRequirements,
      css: 'S3',
    },
    {
      name: 'operationStatus',
      value: (x) => ensureString(x.operationStatus),
      column: 'operationStatus',
    },
    {
      name: 'lastDisbDate',
      value: (x) => customDateStr(x.lastDisbDate),
      column: 'lastDisbursementDate',
    },
    {
      name: 'disbursedPercentage',
      value: (x) => (x.disbursedPercentage ? `${x.disbursedPercentage}%` : ''),
      column: 'disbursementPercentage',
    },
    {
      name: 'lendingTypeModality',
      value: (x) => ensureString(x.lendingTypeModality),
      column: 'lendingTypeModality',
    },
    {
      name: 'miciCases',
      value: (x) => x.miciCases,
      width: 300,
      render: (x, obj) => <Pending textInfo={obj.miciCases !== null ? x : ''} />,
      sortDisabled: true,
    },
    {
      name: 'confirmationOfRegistryToComplainant',
      value: (x) => customDateStr(x.confirmationOfRegistryToComplainant),
      column: 'confirmationOfRegistryToComplainant',
    },
    {
      name: 'applicabilityNotificationDate',
      value: (x) => customDateStr(x.applicabilityNotificationDate),
      column: 'applicabilityNotificationToComplainantDate',
    },
    {
      name: 'listeningSpaceDate',
      value: (x) => isApplicableValue(x.isApplicable, customDateStr(x.listeningSpaceDate)),
    },
    {
      name: 'agreementOnActionPlanDate',
      value: (x) => isApplicableValue(x.isApplicable, customDateStr(x.agreementOnActionPlanDate)),
    },
    {
      name: 'monitoringNotificationDate',
      value: (x) => isApplicableValue(x.isApplicable, customDateStr(x.monitoringNotificationDate)),
    },
    {
      name: 'receptionOfActionPlanDocumentDate',
      value: (x) =>
        isApplicableValue(x.isApplicable, customDateStr(x.receptionOfActionPlanDocumentDate)),
    },
    {
      name: 'verificationWithComplainantDate',
      value: (x) =>
        isApplicableValue(x.isApplicable, customDateStr(x.verificationWithComplainantDate)),
    },
    {
      name: 'authorizationOfClosure',
      value: (x) => isApplicableValue(x.isApplicable, customDateStr(x.authorizationOfClosure)),
    },
    {
      name: 'closureNotificationDate',
      value: (x) => isApplicableValue(x.isApplicable, customDateStr(x.closureNotificationDate)),
    },
    {
      name: 'closureType',
      value: (x) => isApplicableValue(x.isApplicable, x.closureType),
    },
    {
      name: 'additionalActions',
      value: (x) => isApplicableValue(x.isApplicable, formatYesNo(x.additionalActions)),
    },
    {
      name: 'isClaimantMICIInformed',
      value: (x) => isApplicableValue(x.isApplicable, formatYesNo(x.isClaimantMICIInformed)),
    },
    {
      name: 'handledByMICIAfterwards',
      value: (x) => isApplicableValue(x.isApplicable, formatYesNo(x.handledByMICIAfterwards)),
    },
  ];

  return cells.filter((c) => c.name && (!c.disable || !c.disable()));
};

export default getCells;
